import { observable, action, computed } from "mobx";
import { BaseListStore, BaseListState } from "./BaseListStore";

import { CustomerEndpoints } from "../endpoints/CustomerEndpoints";

import { SimpleCustomer, Customer, CustomerTypeWithBoth } from "../model/Customer";
import { CustomerRequest } from "../requests/CustomerRequests";
import { ListWrapper, EmptyWrapper } from "../model/ListWrapper";

export interface CustomerListFilters {
    selectedSales: Array<string>;
    selectedRoles: Array<string>;
    type: CustomerTypeWithBoth;
    fullTextSearch: string;
    includeArchived: boolean;
}

export interface CustomerListState extends BaseListState {
    sortField?: "firstName" | "lastName" | "businessName" | "email";
}

export class CustomerListStore extends BaseListStore<CustomerListFilters, CustomerListState, SimpleCustomer> {
    @observable private _similarCustomers: ListWrapper<SimpleCustomer> = EmptyWrapper();
    private mostRecentSimilarCustomersRequest: number = 1;

    constructor(private customerEndpoints: CustomerEndpoints) {
        super();
    }

    /**
     *
     * @param filters
     * @deprecated Use updateListFilters instead
     */
    @action updateCustomerListFilters(filters: Partial<CustomerListFilters>) {
        this.updateListFilters(filters);
    }
    /**
     *
     * @param state
     * @deprecated Use updateListState instead
     */
    @action updateCustomerListState(state: Partial<CustomerListState>) {
        this.updateListState(state);
    }

    @action async refreshSimilarContacts(firstName: string, lastName: string): Promise<ListWrapper<SimpleCustomer>> {
        this.setLoading();
        try {
            const requestNo = ++this.mostRecentSimilarCustomersRequest;
            const similarCustomers = await this.customerEndpoints.getCustomerList(10, 0, undefined, { firstName, lastName });

            // Only update if this is the most recent request
            if (requestNo === this.mostRecentSimilarCustomersRequest) {
                this._similarCustomers = similarCustomers;
                this.setReady();
            }
            return this._similarCustomers;
        } catch (e) {
            this.setReady();
            throw e;
        }
    }

    @action async refreshSimilarBusinesses(businessName: string): Promise<ListWrapper<SimpleCustomer>> {
        this.setLoading();
        try {
            const requestNo = ++this.mostRecentSimilarCustomersRequest;
            const similarCustomers = await this.customerEndpoints.getCustomerList(10, 0, undefined, { businessName });

            // Only update if this is the most recent request
            if (requestNo === this.mostRecentSimilarCustomersRequest) {
                this._similarCustomers = similarCustomers;
                this.setReady();
            }
            return this._similarCustomers;
        } catch (e) {
            this.setReady();
            throw e;
        }
    }

    @action async addCustomer(request: CustomerRequest): Promise<Customer> {
        this.setLoading();
        try {
            const newCustomer = await this.customerEndpoints.createCustomer(request);

            // Now that we've created a new customer, let the store know that we should
            // refresh our list.
            this.refresh();

            return newCustomer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async deleteCustomer(customerId: number): Promise<void> {
        this.setLoading();
        try {
            await this.customerEndpoints.deleteCustomer(customerId);
            this.refresh();
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async updateArchivedStatus(customerId: number, archived: boolean): Promise<Customer> {
        this.setLoading();
        try {
            const archivedCustomer = await this.customerEndpoints.editCustomer(customerId, {
                requests: [{ field: "archived", value: archived + "" }]
            });
            this.refresh();
            return archivedCustomer;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated Use listFilters instead
     */
    @computed get customerListFilters(): CustomerListFilters {
        return this.listFilters;
    }

    /**
     * @deprecated Use listState instead
     */
    @computed get customerListState(): CustomerListState {
        return this.listState;
    }

    /**
     * @deprecated Use list instead
     */
    @computed get customerList(): ListWrapper<SimpleCustomer> {
        return this.list;
    }

    @computed get similarCustomers(): ListWrapper<SimpleCustomer> {
        return this._similarCustomers;
    }

    public defaultFilterFullTextSearch() {
        this.updateCustomerListFilters({ fullTextSearch: "" });
    }

    protected obtainList(): Promise<ListWrapper<SimpleCustomer>> {
        const state = this._listState,
            filters = this._listFilters;
        return this.customerEndpoints.getCustomerList(
            state.sizePerPage,
            (state.page - 1) * state.sizePerPage,
            state.sortField && state.sortOrder ? `${state.sortField} ${state.sortOrder}` : undefined,
            {
                type: filters.type,
                fullTextSearch: filters.fullTextSearch,
                involvementSaleIds: filters.selectedSales.join(","),
                involvementRoleIds: filters.selectedRoles.join(","),
                includeArchived: filters.includeArchived
            }
        );
    }

    protected defaultFilters(): CustomerListFilters {
        return {
            selectedSales: [],
            selectedRoles: [],
            type: "BOTH",
            fullTextSearch: "",
            includeArchived: false
        };
    }

    protected defaultState(): CustomerListState {
        // Nothing added here
        return super.defaultState();
    }
}



// WEBPACK FOOTER //
// ./src/stores/CustomerListStore.ts